// src/pages/AuthPage.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Grid,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  Timestamp,
  collection,
  getDocs,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { getFunctions } from "firebase/functions";
import { RESERVED_SLUGS } from "../constants/reservedSlugs";

const functions = getFunctions();
const firestore = getFirestore();

const AuthPage = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [shopName, setShopName] = useState("");
  const [shopAddress, setShopAddress] = useState({
    street: "",
    city: "",
    postalCode: "",
    country: "Philippines",
  });
  const [shopPhone, setShopPhone] = useState("");
  const [shopSlug, setShopSlug] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState("");

  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAffiliates = async () => {
      const snapshot = await getDocs(collection(firestore, "affiliates"));
      const results = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAffiliates(results);
    };
    if (isSignUp) fetchAffiliates();
  }, [isSignUp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isSignUp) {
        const finalSlug = shopSlug.trim().toLowerCase().replace(/\s+/g, "-");

        if (RESERVED_SLUGS.includes(finalSlug)) {
          setError("This slug is reserved. Please choose a different one.");
          setLoading(false);
          return;
        }

        const shopRef = doc(firestore, "rentalShops", finalSlug);
        const shopSnap = await getDoc(shopRef);

        if (shopSnap.exists()) {
          setError("This URL slug is already taken.");
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        const user = userCredential.user;

        const currency = shopAddress.country === "Switzerland" ? "CHF" : "PHP";

        await setDoc(shopRef, {
          name: shopName,
          slug: finalSlug,
          description: null,
          imageUrl:
            "https://firebasestorage.googleapis.com/v0/b/vrental-ph.firebasestorage.app/o/shopImages%2Fdefault.png?alt=media&token=c2da2753-353c-4501-9eca-a51a251567d0",
          address: shopAddress,
          fullAddress: `${shopAddress.street}, ${shopAddress.city} ${shopAddress.postalCode}, ${shopAddress.country}`,
          phone: shopPhone,
          email: email,
          location: { lat: null, lng: null },
          support: {
            affiliateId: selectedAffiliateId || null,
          },
          operatingHours: {
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: [],
            Saturday: [],
            Sunday: [],
          },
          deliveryToHotel: {
            active: false,
            price: 0,
          },
          dropOffLocations: [],
          payouts: [],
          rules: {
            freeExtraHours: 0,
            minRentalDurationHours: 2,
            maxRentalDurationHours: null,
            allowSameDayBookings: true,
            bufferTimeBeforeBookingHours: 0,
            sameDayBookingCutoff: null,
          },
          meta: {
            ownerId: user.uid,
            affiliateId: selectedAffiliateId || null,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            status: "active",
            currency,
          },
        });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        const user = auth.currentUser;
        const userDocRef = doc(firestore, "users", user.uid);
        const userSnap = await getDoc(userDocRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          const role = userData.role;

          if (role === "admin") {
            window.location.href = "https://admin.ridecan.com/dashboard";
          } else if (role === "affiliate") {
            window.location.href = "https://affiliate.ridecan.com/dashboard";
          } else if (role === "shopOwner") {
            window.location.href = "https://shop.ridecan.com";
          } else {
            setError("Unrecognized role.");
          }
        } else {
          setError("User data not found.");
        }
      }
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, p: 4, boxShadow: 3 }}>
        <Typography variant="h4" align="center" gutterBottom>
          {isSignUp ? "Sign Up" : "Login"}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {isSignUp && (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Shop Name"
                value={shopName}
                onChange={(e) => setShopName(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Street"
                value={shopAddress.street}
                onChange={(e) =>
                  setShopAddress((prev) => ({
                    ...prev,
                    street: e.target.value,
                  }))
                }
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="City"
                value={shopAddress.city}
                onChange={(e) =>
                  setShopAddress((prev) => ({ ...prev, city: e.target.value }))
                }
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Postal Code"
                value={shopAddress.postalCode}
                onChange={(e) =>
                  setShopAddress((prev) => ({
                    ...prev,
                    postalCode: e.target.value,
                  }))
                }
              />
              <TextField
                select
                margin="normal"
                required
                fullWidth
                label="Country"
                value={shopAddress.country}
                onChange={(e) =>
                  setShopAddress((prev) => ({
                    ...prev,
                    country: e.target.value,
                  }))
                }
              >
                <MenuItem value="Philippines">Philippines</MenuItem>
                <MenuItem value="Switzerland">Switzerland</MenuItem>
              </TextField>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Phone Number"
                type="tel"
                value={shopPhone}
                onChange={(e) => setShopPhone(e.target.value)}
                helperText="Include country code (e.g., +63)"
              />
              <TextField
                margin="normal"
                fullWidth
                required
                label="Custom URL (Slug)"
                value={shopSlug}
                onChange={(e) =>
                  setShopSlug(
                    e.target.value
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                      .replace(/[^a-z0-9\-]/g, "")
                  )
                }
                helperText="Example: 'robin-rentals'"
              />
              <Typography variant="caption" sx={{ mt: 1 }}>
                Final URL: ridecan.com/
                {shopSlug
                  ? shopSlug.toLowerCase().replace(/\s+/g, "-")
                  : shopName.toLowerCase().replace(/\s+/g, "-")}
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Customer Service Contact
              </Typography>
              <TextField
                select
                label="Select an Affiliate"
                value={selectedAffiliateId}
                onChange={(e) => setSelectedAffiliateId(e.target.value)}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {affiliates.map((affiliate) => (
                  <option key={affiliate.id} value={affiliate.id}>
                    {affiliate.name}
                  </option>
                ))}
              </TextField>
            </>
          )}
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : isSignUp ? (
              "Sign Up"
            ) : (
              "Login"
            )}
          </Button>
          <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
            <Grid item>
              <Link
                component="button"
                variant="body2"
                onClick={() => setIsSignUp((prev) => !prev)}
              >
                {isSignUp
                  ? "Already have an account? Login"
                  : "Don't have an account? Sign Up"}
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default AuthPage;
